import moment from 'moment';
import 'moment-timezone';

class DateHelper {
  /**
   * Get the localized timezone.
   */
  static getLocalizedTimezone(): string {
    return 'America/New_York';
  }

  /**
   * Get date time in UTC from timestamp.
   * @param timestamp
   */
  static getUtcDateTimeFromTimestamp(timestamp: number): moment.Moment {
    return moment.unix(timestamp)
      .utc();
  }

  /**
   * Get date time in local timezone.
   * @param dateTime
   */
  static getLocalizedDateTime(dateTime: moment.Moment): moment.Moment {
    return dateTime.tz(this.getLocalizedTimezone());
  }

  /**
   * Get date time in local timezone from timestamp.
   * @param timestamp
   */
  static getLocalizedDateTimeFromTimestamp(timestamp: number): moment.Moment {
    const utcDateTime = this.getUtcDateTimeFromTimestamp(timestamp);
    return this.getLocalizedDateTime(utcDateTime);
  }

  /**
   * Determine if we need to provide a plural version of a given unit (ie: week or weeks)
   * @param numOfUnits int
   * @param unit string
   */
  static pluralizeUnits(numOfUnits, unit = '') {
    // With counted nouns, zero is always followed by plural nouns.
    return `${numOfUnits} ${unit}${numOfUnits === 1 ? '' : 's'}`;
  }

  /**
   * Return a human-readable date diff.
   *
   * If under a week old, then display “X days old”
   * If over a week old and less than 20 week sold, display “X weeks old”
   * At 20 weeks old, show “X Months X weeks old”
   * e.g. at 20 weeks it shows “5 months old”,
   * At 21 weeks it shows “5 months 1 week old”
   */
  static getHumanReadableAge(utcString: string): string {
    const birthDate = moment.utc(utcString);
    const birthDateString = birthDate.format('YYYY-MM-DD');
    const then = moment.tz(birthDateString, this.getLocalizedTimezone());
    const now = moment();
    const diff = now.diff(then, 'days');

    if (diff === 0) {
      return 'born today';
    }

    if (diff < 7) {
      return `${this.pluralizeUnits(diff, 'day')} old`;
    }
    if (diff < 20 * 7) {
      // NOTE: Round down since they haven't turned the next "week" old yet.
      return `${this.pluralizeUnits(Math.floor(diff / 7), 'week')} old`;
    }

    const months = now.diff(then, 'months');
    const weeks = now.subtract(months, 'months')
      .diff(then, 'week');
    if (weeks === 0) {
      return `${this.pluralizeUnits(months, 'month')} old`;
    }
    return `${this.pluralizeUnits(months, 'month')} ${this.pluralizeUnits(weeks, 'week')} old`;
  }
}

export default DateHelper;
